import React, { useRef, useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import { Grid, Typography } from '@mui/material';

interface FileUploadProp {
  width?: string;
  fileUploadHandler: (file: File | null) => void;
  buttonText?: string;
  enabled?: boolean;
  deleted?: boolean;
  canUpload?: boolean;
  onUploadConditionFail?: () => void;
  oldFilePath?: string;
}

const FileUpload: React.FC<FileUploadProp> = ({
  width = '45%',
  fileUploadHandler,
  buttonText = 'Resume Upload',
  enabled = true,
  deleted = false,
  canUpload = true,
  onUploadConditionFail,
  oldFilePath,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  useEffect(() => {
    if (deleted) {
      setFileName(null);
    } else if (typeof oldFilePath === 'string' && oldFilePath) {

      const oldFileName = oldFilePath.split('\\').pop();
      setFileName(oldFileName as string);
    } else {
      setFileName(null);
    }
  }, [deleted, oldFilePath]);

  const handleButtonClick = () => {
    if (canUpload) {
      inputRef.current?.click();
    } else {
      onUploadConditionFail?.();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const MAX_FILE_SIZE_MB = 10; // 10 MB

    if (file) {
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        alert(`File is too large. Max allowed size is ${MAX_FILE_SIZE_MB} MB.`);
        return;
      }
      setFileName(file.name);
      fileUploadHandler(file);
      event.target.value = '';
    } else {
      setFileName(null);
    }
  };

  return (
    <Grid item container flexDirection="row" justifyItems="flex-start">
      <Grid item xs={12} sm={8} md={8} sx={{ textAlign: 'left' }}>
        <input
          type="file"
          ref={inputRef}
          onChange={handleFileChange}
          accept=".pdf"
          style={{ display: 'none' }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleButtonClick}
          sx={{ 
            bgcolor: 'var(--ttg-black)', 
            width: { xs: '160px', sm: width },
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
          }}
        >
          {buttonText}
        </Button>
        <Typography sx={{ fontSize: { xs: '0.8rem', sm: '1rem' }, mt: 0.5, wordBreak: 'break-word' }}>
          {fileName && !deleted ? fileName : ''}
        </Typography>
      </Grid>
    </Grid>
  );
};

export default FileUpload;
